/**
 * @component ry-combobox
 * @category  form
 * @summary   Searchable single-select dropdown (Archetype B). Typing in the input filters the options by substring; arrow keys + Enter commit; Escape closes. Companion to `<ry-select>` (which has no text filter). Emits `ry:input` on every keystroke, `ry:open`/`ry:close` on visibility transitions, and `ry:change` with `{value, label}` on selection.
 *
 * Reference: Fomantic UI's dropdown module in "search" mode. ry-combobox is
 * the typeahead variant; `<ry-select>` is the non-filtering picker. The two
 * intentionally stay separate so consumers pick the right one by intent.
 *
 * @attribute placeholder  string?              Input placeholder text.
 * @attribute name         string?              Form field name (passed to the inner input for native form submission).
 * @attribute value        string?              Currently-selected option's value (matches a child `<ry-option value="...">`).
 * @attribute disabled     boolean   default=false   Non-interactive (input + dropdown suppressed).
 *
 * @property  value        string    Live value. Setting writes the attribute AND selects the matching option (if any). Setting to an unknown value is a silent no-op (the previous value survives).
 *
 * @method    open()       Open the dropdown panel. No-op if already open. Fires `ry:open`.
 * @method    close()      Close the panel. Clears highlighted state. Fires `ry:close`. No-op if already closed.
 * @method    toggle()     Open if closed; close if open.
 *
 * @event ry:input
 *        detail: { value: string }
 *        fires:  on every input keystroke. `detail.value` is the CURRENT INPUT TEXT (the filter query), NOT the selected option's value. Don't confuse with `ry:change`.
 *
 * @event ry:beforeopen
 *        detail: {}
 *        fires:  at the top of `open()` — input focus, ArrowDown, arrow click, or `el.open()` — before any state change
 *        cancelable: preventDefault() keeps the panel closed; `ry:open` does not fire
 *
 * @event ry:open
 *        detail: {}
 *        fires:  on transition from closed to open.
 *
 * @event ry:beforeclose
 *        detail: {}
 *        fires:  at the top of `close()` — Escape, Tab, outside click, option commit, or `el.close()` — before any state change
 *        cancelable: preventDefault() keeps the panel open; `ry:close` does not fire
 *
 * @event ry:close
 *        detail: {}
 *        fires:  on transition from open to closed.
 *
 * @event ry:beforechange
 *        detail: { value: string, label: string, previous: string }
 *        fires:  before an option commits — click, Enter on a highlighted option, or `el.value = x` — with the value ABOUT to be set and the current value as `previous`; before the input text, native select, or `value` attribute change
 *        cancelable: preventDefault() leaves the selection unchanged (input text, attribute, aria-selected all untouched); `ry:change` does not fire
 *
 * @event ry:change
 *        detail: { value: string, label: string }
 *        fires:  when the user commits an option (click or Enter on a highlighted option). `detail.value` is the option's `value` attribute; `detail.label` is its text content.

 *
 * @keyboard  Arrow Down — open dropdown if closed, else move highlight to next visible option. Arrow Up — move highlight up. Enter — commit highlighted option. Escape — close without committing; clears the highlight. Tab — close + commit if exact match; otherwise close without committing.
 *
 * @state     `data-ry-state` of `"open"` or `"closed"` on the host. `data-highlighted` on the currently-highlighted option (one at a time during keyboard nav).
 *
 * @content   `<ry-option value="X">Label</ry-option>` children. Options without `value` are valid but won't be retrievable by the `value` setter. Use `data-disabled` on an option to make it non-selectable.
 *
 * @dom
 *   ry-combobox[name?][placeholder?][value?][disabled?][data-ry-state="open|closed"]
 *     > div.ry-combobox__wrapper      [data-ry-target="input-wrapper"]   role="combobox"  aria-expanded
 *       > input.ry-combobox__input    [data-ry-target="input"]   type="text"  placeholder=placeholder
 *     > div.ry-combobox__panel        [data-ry-target="dropdown"]   role="listbox"  hidden when closed
 *       > div.ry-combobox__option     [data-ry-target="option"]   data-value=value  data-highlighted? hidden?
 *         > {option label text}
 *
 * @gotcha  `ry:input` fires the FILTER QUERY (what the user typed), not the SELECTED VALUE. Use `ry:change` for the committed selection. Conflating the two is the most common mistake.
 *
 * @gotcha  Typing into the input filters options by case-insensitive substring match on the option text — NOT the value. To match by value, set the option's text to include the value.
 *
 * @gotcha  Setting `el.value = 'unknown'` (a value that doesn't match any child option) is a SILENT no-op. The previous value remains. There is no warning. Verify your value matches an option before setting.
 *
 * @gotcha  `<ry-option>` is parsed at setup. Dynamic option insertion AFTER mount is NOT supported — the wrapper builds its internal options list once. Re-mount or fully replace the element to change options.
 *
 * @gotcha  When the dropdown opens and a `value` is already set, the matching option is auto-highlighted via the keyboard nav index. Pressing Enter commits the same value (idempotent) and fires `ry:change` again.
 *
 * @example basic
 * ```html
 * <ry-combobox placeholder="Search countries..." name="country">
 *   <ry-option value="us">United States</ry-option>
 *   <ry-option value="uk">United Kingdom</ry-option>
 *   <ry-option value="ca">Canada</ry-option>
 * </ry-combobox>
 * ```
 *
 * @example pre-selected
 * ```html
 * <ry-combobox placeholder="Pick one" value="ca">
 *   <ry-option value="us">United States</ry-option>
 *   <ry-option value="ca">Canada</ry-option>
 * </ry-combobox>
 * ```
 *
 * @example disabled
 * ```html
 * <ry-combobox placeholder="Locked" disabled>
 *   <ry-option value="x">x</ry-option>
 * </ry-combobox>
 * ```
 *
 * @example with-disabled-option
 * ```html
 * <ry-combobox placeholder="Pick a plan">
 *   <ry-option value="free">Free</ry-option>
 *   <ry-option value="pro">Pro</ry-option>
 *   <ry-option value="ent" data-disabled>Enterprise (locked)</ry-option>
 * </ry-combobox>
 * ```
 *
 * @tutorial mount-transforms-to-input-and-panel
 * ```js
 * const el = mount(`
 *   <ry-combobox placeholder="x">
 *     <ry-option value="a">Apple</ry-option>
 *     <ry-option value="b">Banana</ry-option>
 *   </ry-combobox>
 * `);
 * await expectEqual(!!el.querySelector('[data-ry-target="input"]'), true, 'input rendered');
 * await expectEqual(!!el.querySelector('[data-ry-target="dropdown"]'), true, 'panel rendered');
 * await expectEqual(el.querySelectorAll('[data-ry-target="option"]').length, 2);
 * ```
 *
 * @tutorial starts-closed
 * ```js
 * const el = mount('<ry-combobox><ry-option value="a">A</ry-option></ry-combobox>');
 * await expectAttr(el, 'data-ry-state', 'closed');
 * ```
 *
 * @tutorial open-and-close-methods
 * ```js
 * const el = mount('<ry-combobox><ry-option value="a">A</ry-option></ry-combobox>');
 * const fired = { open: 0, close: 0 };
 * el.addEventListener('ry:open', () => fired.open++);
 * el.addEventListener('ry:close', () => fired.close++);
 * el.open();
 * await expectAttr(el, 'data-ry-state', 'open');
 * await expectEqual(fired.open, 1);
 * el.close();
 * await expectAttr(el, 'data-ry-state', 'closed');
 * await expectEqual(fired.close, 1);
 * ```
 *
 * @tutorial typing-filters-by-substring
 * ```js
 * const el = mount(`
 *   <ry-combobox placeholder="x">
 *     <ry-option value="a">Apple</ry-option>
 *     <ry-option value="b">Banana</ry-option>
 *     <ry-option value="c">Cherry</ry-option>
 *   </ry-combobox>
 * `);
 * const input = el.querySelector('[data-ry-target="input"]');
 * input.value = 'an';
 * input.dispatchEvent(new Event('input', { bubbles: true }));
 * // Banana matches 'an'
 * const visible = Array.from(el.querySelectorAll('[data-ry-target="option"]:not([hidden])'));
 * await expectEqual(visible.length, 1, 'one option matches "an"');
 * await expectEqual(visible[0].textContent.trim(), 'Banana');
 * ```
 *
 * @tutorial filter-is-case-insensitive-substring
 * ```js
 * const el = mount(`
 *   <ry-combobox>
 *     <ry-option value="a">Apple</ry-option>
 *     <ry-option value="b">Banana</ry-option>
 *   </ry-combobox>
 * `);
 * const input = el.querySelector('[data-ry-target="input"]');
 * input.value = 'AP';
 * input.dispatchEvent(new Event('input', { bubbles: true }));
 * const visible = Array.from(el.querySelectorAll('[data-ry-target="option"]:not([hidden])'));
 * await expectEqual(visible.length, 1, 'case-insensitive substring matches "AP" → Apple');
 * ```
 *
 * @tutorial input-fires-ry-input-with-filter-text-not-value
 * ```js
 * // Wrong assumption: ry:input fires the selected value. It fires the
 * // raw filter query — for committed selection use ry:change.
 * const el = mount(`
 *   <ry-combobox>
 *     <ry-option value="a">Apple</ry-option>
 *   </ry-combobox>
 * `);
 * const input = el.querySelector('[data-ry-target="input"]');
 * const fired = [];
 * el.addEventListener('ry:input', (e) => fired.push(e.detail.value));
 * input.value = 'q';
 * input.dispatchEvent(new Event('input', { bubbles: true }));
 * await expectEqual(fired[0], 'q', 'detail.value is the filter query');
 * ```
 *
 * @tutorial click-option-commits-and-fires-ry-change
 * ```js
 * const el = mount(`
 *   <ry-combobox>
 *     <ry-option value="us">United States</ry-option>
 *     <ry-option value="uk">United Kingdom</ry-option>
 *   </ry-combobox>
 * `);
 * el.open();
 * const fired = [];
 * el.addEventListener('ry:change', (e) => fired.push(e.detail));
 * const ukOption = Array.from(el.querySelectorAll('[data-ry-target="option"]'))
 *   .find((o) => o.dataset.value === 'uk');
 * ukOption.click();
 * await expectEqual(el.value, 'uk');
 * await expectEqual(fired.length, 1);
 * await expectEqual(fired[0].value, 'uk');
 * await expectEqual(fired[0].label, 'United Kingdom');
 * ```
 *
 * @tutorial value-setter-selects-matching-option
 * ```js
 * const el = mount(`
 *   <ry-combobox>
 *     <ry-option value="a">Apple</ry-option>
 *     <ry-option value="b">Banana</ry-option>
 *   </ry-combobox>
 * `);
 * el.value = 'b';
 * await expectEqual(el.value, 'b');
 * const input = el.querySelector('[data-ry-target="input"]');
 * await expectEqual(input.value, 'Banana', 'input text mirrors selected label');
 * ```
 *
 * @tutorial value-setter-unknown-is-silent-noop
 * ```js
 * // Wrong assumption: setting an unknown value clears the selection or
 * // throws. It silently no-ops — the previous selection survives.
 * const el = mount(`
 *   <ry-combobox value="a">
 *     <ry-option value="a">Apple</ry-option>
 *     <ry-option value="b">Banana</ry-option>
 *   </ry-combobox>
 * `);
 * el.value = 'zzz-unknown';
 * await expectEqual(el.value, 'a', 'previous value survives unknown set');
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange leaves the selection untouched —
 * // value attribute, input text, and ry:change all stay as they were.
 * // detail carries the value about to commit plus `previous`.
 * const el = mount(`
 *   <ry-combobox value="a">
 *     <ry-option value="a">Apple</ry-option>
 *     <ry-option value="b">Banana</ry-option>
 *   </ry-combobox>
 * `);
 * const changed = [];
 * el.addEventListener('ry:change', (e) => changed.push(e.detail));
 * let seen = null;
 * el.addEventListener('ry:beforechange', (e) => { seen = e.detail; e.preventDefault(); }, { once: true });
 * // Grab the option BEFORE open() — the panel portals to <body> on open.
 * const banana = Array.from(el.querySelectorAll('[data-ry-target="option"]')).find((o) => o.dataset.value === 'b');
 * el.open();
 * banana.click();

 * await expectEqual(el.value, 'a', 'vetoed commit leaves the value unchanged');
 * await expectEqual(el.querySelector('[data-ry-target="input"]').value, 'Apple', 'input text unchanged');
 * await expectEqual(changed.length, 0, 'ry:change did not fire');
 * await expectEqual(seen.value, 'b');
 * await expectEqual(seen.previous, 'a');
 * banana.click();
 * await expectEqual(el.value, 'b', 'without the veto, the click commits');
 * await expectEqual(changed.length, 1);
 * ```
 *
 * @tutorial beforeopen-veto
 * ```js
 * // preventDefault() on ry:beforeopen keeps the panel closed; ry:open is silent.
 * const el = mount(`
 *   <ry-combobox>
 *     <ry-option value="a">Apple</ry-option>
 *   </ry-combobox>
 * `);
 * const opened = [];
 * el.addEventListener('ry:open', () => opened.push(1));
 * el.addEventListener('ry:beforeopen', (e) => e.preventDefault(), { once: true });
 * el.open();
 * await expectEqual(el.state, 'closed', 'vetoed open leaves state closed');
 * await expectEqual(opened.length, 0, 'ry:open did not fire');
 * el.open();
 * await expectEqual(el.state, 'open', 'without the veto, open() works');
 * ```
 *
 * @tutorial beforeclose-veto
 * ```js
 * // preventDefault() on ry:beforeclose keeps the panel open; ry:close is silent.
 * const el = mount(`
 *   <ry-combobox>
 *     <ry-option value="a">Apple</ry-option>
 *   </ry-combobox>
 * `);
 * el.open();
 * const closed = [];
 * el.addEventListener('ry:close', () => closed.push(1));
 * el.addEventListener('ry:beforeclose', (e) => e.preventDefault(), { once: true });
 * el.close();
 * await expectEqual(el.state, 'open', 'vetoed close leaves state open');
 * await expectEqual(closed.length, 0, 'ry:close did not fire');
 * el.close();
 * await expectEqual(el.state, 'closed', 'without the veto, close() works');
 * ```
 *
 * @see ry-select
 * @see ry-dropdown
 */


import { RyElement } from '../core/ry-element.js';
import { attachFloating } from '../core/ry-floating.js';
import type { SelectOption, ChangeDetail, BeforeChangeDetail } from '../types.js';


let comboboxIdCounter = 0;

export class RyCombobox extends RyElement {
  #comboboxId: string | null = null;
  #highlightedIndex = -1;
  #options: SelectOption[] = [];
  #floatingCleanup: (() => void) | null = null;
  #dropdownEl: HTMLElement | null = null;
  #portalRestore: (() => void) | null = null;

  static observedAttributes = ['value', 'disabled'] as const;

  setup(): void {
    this.#comboboxId = `ry-combobox-${++comboboxIdCounter}`;
    this.#transformMarkup();

    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');

    if (input) {
      this.on(input, 'input', this.#handleInputEvent);
      this.on(input, 'focus', this.#handleFocus);
    }

    // Capture dropdown reference up-front — after open() it portals to body,
    // but we always query options/highlight through #scope() which prefers
    // this captured ref.
    this.#dropdownEl = this.$<HTMLElement>('[data-ry-target="dropdown"]');

    this.on(this, 'click', this.#handleClick);
    this.on(this, 'keydown', this.#handleKeydown);
    this.on(this, 'mouseover', this.#handleMouseover);
    if (this.#dropdownEl) {
      // Option clicks need to fire even when the dropdown is portalled away.
      this.on(this.#dropdownEl, 'click', this.#handleClick);
      this.on(this.#dropdownEl, 'mouseover', this.#handleMouseover);
    }
    this.on(document, 'click', this.#handleOutsideClick);

    // RyElement.state setter no-ops when newState === current default
    // ('closed' from the getter), so write the data attribute directly to
    // ensure consumers + CSS see an explicit data-ry-state="closed" at mount.
    if (!this.hasAttribute('data-ry-state')) {
      this.dataset.ryState = 'closed';
    }

    const initialValue = this.getAttribute('value');
    if (initialValue) {
      this.value = initialValue;
    }
  }

  #transformMarkup(): void {
    const options: SelectOption[] = [...this.querySelectorAll('ry-option')].map(opt => ({
      value: opt.getAttribute('value') ?? opt.textContent?.trim() ?? '',
      label: opt.textContent?.trim() ?? '',
      disabled: opt.hasAttribute('disabled')
    }));

    const placeholder = this.getAttribute('placeholder') ?? 'Search...';
    const name = this.getAttribute('name') ?? '';
    const disabled = this.hasAttribute('disabled');

    const nativeOptions = options.map(opt =>
      `<option value="${opt.value}"${opt.disabled ? ' disabled' : ''}>${opt.label}</option>`
    ).join('');

    const listboxOptions = options.map((opt, i) => `
      <div data-ry-target="option" class="ry-combobox__option"
           role="option"
           id="${this.#comboboxId}-option-${i}"
           data-value="${opt.value}"
           ${opt.disabled ? 'data-disabled' : ''}
           aria-disabled="${opt.disabled}">
        ${opt.label}
      </div>
    `).join('');

    this.innerHTML = `
      <div data-ry-target="input-wrapper" class="ry-combobox__input-wrapper"
           role="combobox"
           aria-expanded="false"
           aria-haspopup="listbox"
           aria-owns="${this.#comboboxId}-listbox">
        <input data-ry-target="input" class="ry-combobox__input"
               type="text"
               placeholder="${placeholder}"
               autocomplete="off"
               aria-autocomplete="list"
               aria-controls="${this.#comboboxId}-listbox"
               ${disabled ? 'disabled' : ''}>
        <span data-ry-target="arrow" class="ry-combobox__arrow">&#9662;</span>
      </div>
      <div data-ry-target="dropdown" class="ry-combobox__dropdown"
           role="listbox"
           id="${this.#comboboxId}-listbox"
           tabindex="-1">
        ${listboxOptions}
        <div data-ry-target="empty" class="ry-combobox__empty" hidden>
          No results
        </div>
      </div>
      <select data-ry-target="native" class="ry-combobox__native"
              ${name ? `name="${name}"` : ''}
              tabindex="-1" aria-hidden="true"
              ${disabled ? 'disabled' : ''}>
        <option value="">${placeholder}</option>
        ${nativeOptions}
      </select>
    `;

    this.#options = options;
  }

  #handleInputEvent = (): void => {
    // Open the dropdown FIRST (open() clears all `[hidden]` attributes to
    // recover from a prior filter session), THEN apply the new filter.
    // Reversing this order silently undoes the filter — see iter 35 fix.
    if (this.state !== 'open') {
      this.open();
    }

    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
    if (!input) return;

    const query = input.value.trim();
    this.#filterOptions(query);

    this.emit('input', { value: query });
  };

  #filterOptions(query: string): void {
    const options = this.#scope().querySelectorAll<HTMLElement>('[data-ry-target="option"]');
    const lowerQuery = query.toLowerCase();
    let visibleCount = 0;

    options.forEach(opt => {
      const label = opt.textContent?.trim().toLowerCase() ?? '';
      const matches = !query || label.includes(lowerQuery);
      if (matches && !opt.hasAttribute('data-disabled')) {
        opt.removeAttribute('hidden');
        visibleCount++;
      } else {
        opt.setAttribute('hidden', '');
      }
    });

    const emptyEl = this.$('[data-ry-target="empty"]');
    if (emptyEl) {
      if (visibleCount === 0) {
        emptyEl.removeAttribute('hidden');
      } else {
        emptyEl.setAttribute('hidden', '');
      }
    }

    this.#highlightedIndex = -1;
    this.#scope().querySelectorAll('[data-ry-target="option"][data-highlighted]').forEach(opt => {
      opt.removeAttribute('data-highlighted');
    });
  }

  #handleClick = (event: MouseEvent): void => {
    if (this.hasAttribute('disabled')) return;

    const target = event.target as Element;
    const option = target.closest('[data-ry-target="option"]');
    if (option instanceof HTMLElement && !option.hasAttribute('data-disabled')) {
      this.#selectOption(option);
      return;
    }

    const arrow = target.closest('[data-ry-target="arrow"]');
    if (arrow) {
      this.toggle();
      return;
    }
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (this.hasAttribute('disabled')) return;
    const isOpen = this.state === 'open';

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          this.open();
        } else {
          this.#highlightNext();
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (isOpen) {
          this.#highlightPrev();
        }
        break;

      case 'Enter':
        if (isOpen && this.#highlightedIndex >= 0) {
          event.preventDefault();
          const visibleOptions = this.#scope().querySelectorAll<HTMLElement>(
            '[data-ry-target="option"]:not([hidden]):not([data-disabled])'
          );
          const option = visibleOptions[this.#highlightedIndex];
          if (option) {
            this.#selectOption(option);
          }
        }
        break;

      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          this.close();
        }
        break;

      case 'Tab':
        if (isOpen) {
          this.close();
        }
        break;
    }
  };

  #handleMouseover = (event: MouseEvent): void => {
    const target = event.target as Element;
    const option = target.closest('[data-ry-target="option"]');
    if (!(option instanceof HTMLElement) || option.hasAttribute('data-disabled') || option.hasAttribute('hidden')) return;

    const visibleOptions = Array.from(this.#scope().querySelectorAll<HTMLElement>(
      '[data-ry-target="option"]:not([hidden]):not([data-disabled])'
    ));
    const index = visibleOptions.indexOf(option);
    if (index >= 0) {
      this.#highlightIndex(index);
    }
  };

  #handleOutsideClick = (event: MouseEvent): void => {
    const target = event.target as Node;
    if (this.state !== 'open') return;
    if (this.contains(target)) return;
    // Dropdown is portalled to <body> — treat clicks inside it as inside.
    if (this.#dropdownEl && this.#dropdownEl.contains(target)) return;
    this.close();
  };

  #handleFocus = (): void => {
    if (this.hasAttribute('disabled')) return;

    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
    if (input && input.value) {
      input.select();
    }

    this.open();
  };

  #selectOption(optionEl: HTMLElement): void {
    const value = optionEl.dataset.value ?? '';
    const label = optionEl.textContent?.trim() ?? '';
    const previous = this.value;

    if (!this.emit<BeforeChangeDetail>('beforechange', { value, label, previous })) return;

    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');

    if (input) {
      input.value = label;
    }

    const nativeSelect = this.$<HTMLSelectElement>('[data-ry-target="native"]');
    if (nativeSelect) {
      nativeSelect.value = value;
    }

    this.setAttribute('value', value);

    this.#scope().querySelectorAll('[data-ry-target="option"]').forEach(opt => {
      opt.removeAttribute('aria-selected');
    });
    optionEl.setAttribute('aria-selected', 'true');

    this.close();
    this.emit<ChangeDetail>('change', { value, label });
  }

  #highlightIndex(index: number): void {
    const options = this.#scope().querySelectorAll<HTMLElement>(
      '[data-ry-target="option"]:not([hidden]):not([data-disabled])'
    );
    if (index < 0 || index >= options.length) return;

    this.#scope().querySelectorAll('[data-ry-target="option"][data-highlighted]').forEach(opt => {
      opt.removeAttribute('data-highlighted');
    });

    const option = options[index];
    if (!option) return;

    option.setAttribute('data-highlighted', '');
    // Scroll WITHIN the dropdown only — see ry-select.ts for the same fix.
    // scrollIntoView walks all ancestors and would scroll the page to top
    // when the dropdown is briefly at body top:0 (before FUI writes coords).
    if (this.#dropdownEl) {
      const optTop = (option as HTMLElement).offsetTop;
      const optHeight = (option as HTMLElement).offsetHeight;
      const scrollTop = this.#dropdownEl.scrollTop;
      const visibleHeight = this.#dropdownEl.clientHeight;
      if (optTop < scrollTop) {
        this.#dropdownEl.scrollTop = optTop;
      } else if (optTop + optHeight > scrollTop + visibleHeight) {
        this.#dropdownEl.scrollTop = optTop + optHeight - visibleHeight;
      }
    }
    this.#highlightedIndex = index;

    const wrapper = this.$('[data-ry-target="input-wrapper"]');
    if (wrapper) {
      wrapper.setAttribute('aria-activedescendant', option.id);
    }
  }

  #highlightNext(): void {
    const options = this.#scope().querySelectorAll('[data-ry-target="option"]:not([hidden]):not([data-disabled])');
    const next = Math.min(this.#highlightedIndex + 1, options.length - 1);
    this.#highlightIndex(next);
  }

  #highlightPrev(): void {
    const prev = Math.max(this.#highlightedIndex - 1, 0);
    this.#highlightIndex(prev);
  }

  /** Where to query option/highlight elements from. After portal, the
   *  dropdown is no longer a descendant of `this` — the captured ref still
   *  works. */
  #scope(): Element {
    return this.#dropdownEl ?? this;
  }

  #attachFloating(): void {
    const trigger = this.$<HTMLElement>('[data-ry-target="input-wrapper"]');
    const dropdown = this.#dropdownEl;
    if (!trigger || !dropdown) return;

    // Portal dropdown to <body> so it escapes any ancestor stacking context.
    // CSS keys on the dropdown's OWN [data-ry-state], so we mirror state
    // onto it (the host's data-ry-state no longer reaches the portalled
    // panel via descendant selectors).
    dropdown.setAttribute('data-ry-state', 'open');
    this.#portalRestore = this.portalToBody(dropdown);

    this.#floatingCleanup = attachFloating(trigger, dropdown, {
      placement: 'bottom-start',
      offset: 4,
      flip: true,
      shift: true,
      matchWidth: true,
      reflectPlacementAttribute: true,
    });
  }

  #detachFloating(): void {
    this.#floatingCleanup?.();
    this.#floatingCleanup = null;
    if (this.#dropdownEl) {
      this.#dropdownEl.setAttribute('data-ry-state', 'closed');
    }
    this.#portalRestore?.();
    this.#portalRestore = null;
  }

  open(): void {
    if (this.state === 'open') return;
    if (!this.emit('beforeopen')) return;

    this.state = 'open';

    const wrapper = this.$('[data-ry-target="input-wrapper"]');

    if (wrapper) {
      wrapper.setAttribute('aria-expanded', 'true');
    }

    // Show all options on open (clear filter)
    this.#scope().querySelectorAll<HTMLElement>('[data-ry-target="option"]').forEach(opt => {
      if (!opt.hasAttribute('data-disabled')) {
        opt.removeAttribute('hidden');
      }
    });
    const emptyEl = this.$('[data-ry-target="empty"]');
    if (emptyEl) {
      emptyEl.setAttribute('hidden', '');
    }

    this.#attachFloating();

    // Highlight current value if set
    const currentValue = this.getAttribute('value');
    if (currentValue) {
      const options = Array.from(this.#scope().querySelectorAll<HTMLElement>(
        '[data-ry-target="option"]:not([hidden]):not([data-disabled])'
      ));
      const index = options.findIndex(opt => opt.dataset.value === currentValue);
      if (index >= 0) {
        this.#highlightIndex(index);
      }
    }

    this.emit('open');
  }

  close(): void {
    if (this.state === 'closed') return;
    if (!this.emit('beforeclose')) return;

    this.state = 'closed';
    this.#detachFloating();


    const wrapper = this.$('[data-ry-target="input-wrapper"]');
    if (wrapper) {
      wrapper.setAttribute('aria-expanded', 'false');
      wrapper.removeAttribute('aria-activedescendant');
    }

    this.#scope().querySelectorAll('[data-ry-target="option"][data-highlighted]').forEach(opt => {
      opt.removeAttribute('data-highlighted');
    });
    this.#highlightedIndex = -1;

    this.emit('close');
  }

  toggle(): void {
    if (this.state === 'open') {
      this.close();
    } else {
      this.open();
    }
  }

  get value(): string {
    return this.getAttribute('value') ?? '';
  }

  set value(val: string) {
    const option = this.#scope().querySelector<HTMLElement>(`[data-ry-target="option"][data-value="${val}"]`);
    if (option) {
      this.#selectOption(option);
    }
  }
}

customElements.define('ry-combobox', RyCombobox);
